import { useCallback, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import type { AdminTemplateWriteResult } from "../../api/types";
import { Field } from "../../components/Field";
import { Modal } from "../../components/Modal";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { createTemplate } from "./api";

const EMPTY_CREATE_FORM = { nombre: "", producto: "", temporada: "", html: "" };

type SubmitState = { status: "idle" } | { status: "submitting" } | { status: "error"; message: string };

interface TemplateCreateFormProps {
  onClose: () => void;
  /** Called once after a successful create — lets the caller refresh the templates list. */
  onCreated?: (result: AdminTemplateWriteResult) => void;
}

/**
 * "Nueva plantilla" (design.md §5.5 "Editor de plantillas").
 *
 * Rewritten to the v3 prototype (panel feedback 2026-10-30): opens inside the
 * shared `<Modal>` popup (680px) with a "Quedará activa en su versión 1."
 * subtitle and a header ×, then four flat fields (Nombre, Producto,
 * Temporada, Contenido HTML as a plain textarea) and a Cancelar / Crear
 * plantilla footer. No fieldset legends, divider, code-editor tab or Liquid
 * hint any more.
 *
 * Behavior is unchanged: same `createTemplate` call, same typed-input-
 * preservation-on-error contract, same pending/disabled state. Escape/
 * backdrop/× are ignored while the create request is in flight (a POST in
 * flight is not discardable — same rule as `CampaignCreatePanel`).
 */
export function TemplateCreateForm({ onClose, onCreated }: TemplateCreateFormProps) {
  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  const [submitState, setSubmitState] = useState<SubmitState>({ status: "idle" });
  const isSubmitting = submitState.status === "submitting";

  // `Modal` binds its Escape/backdrop `onClose` once at mount (see Modal.tsx),
  // so the in-flight guard must be read through a ref at call time.
  const submittingRef = useRef(false);
  submittingRef.current = isSubmitting;
  const requestClose = useCallback(() => {
    if (submittingRef.current) return;
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (submitState.status === "submitting") return;
      setSubmitState({ status: "submitting" });
      try {
        const result = await createTemplate(form);
        setForm(EMPTY_CREATE_FORM);
        setSubmitState({ status: "idle" });
        onCreated?.(result);
      } catch (error) {
        // El input tipeado NUNCA se descarta en error — solo en exito.
        setSubmitState({
          status: "error",
          message: error instanceof ApiError ? error.message : "No se pudo crear la plantilla.",
        });
      }
    },
    [form, submitState.status, onCreated],
  );

  return (
    <Modal ariaLabel="Nueva plantilla" onClose={requestClose}>
      <form
        aria-label="Nueva plantilla"
        className="v2-modal-v2 v2-modal-v2--w680 v2-template-create-modal"
        onSubmit={(event) => void handleSubmit(event)}
      >
        <header className="v2-modal-v2__header">
          <div>
            <h2>Nueva plantilla</h2>
            <p className="v2-modal-v2__subtitle">Quedará activa en su versión 1.</p>
          </div>
          <ModalCloseButton onClose={requestClose} disabled={isSubmitting} />
        </header>

        <div className="v2-modal-v2__body">
          <Field label="Nombre" htmlFor="template-nombre">
            <input
              id="template-nombre"
              className="v2-u-target-min"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              disabled={isSubmitting}
              required
            />
          </Field>
          <Field label="Producto" htmlFor="template-producto">
            <input
              id="template-producto"
              className="v2-u-target-min"
              value={form.producto}
              onChange={(e) => setForm({ ...form, producto: e.target.value })}
              disabled={isSubmitting}
              required
            />
          </Field>
          <Field label="Temporada" htmlFor="template-temporada">
            <input
              id="template-temporada"
              className="v2-u-target-min"
              value={form.temporada}
              onChange={(e) => setForm({ ...form, temporada: e.target.value })}
              disabled={isSubmitting}
              required
            />
          </Field>
          <Field label="Contenido HTML" htmlFor="template-html">
            <textarea
              id="template-html"
              className="v2-template-create-modal__html"
              placeholder="<html>…"
              rows={8}
              spellCheck={false}
              value={form.html}
              onChange={(e) => setForm({ ...form, html: e.target.value })}
              disabled={isSubmitting}
              required
            />
          </Field>

          {submitState.status === "error" && <p role="alert">{submitState.message}</p>}
        </div>

        <footer className="v2-modal-v2__footer">
          <button type="button" className="v2-u-target-min v2-btn-secondary" onClick={requestClose} disabled={isSubmitting}>
            Cancelar
          </button>
          <button type="submit" className="v2-u-target-min v2-btn-primary" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? "Creando…" : "Crear plantilla"}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
